import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import styles from "./index.module.css"

/**
 * The wording, by variant and count.
 *
 * Singular is picked by hand rather than by i18next's plural suffixes: the project is on i18next
 * 19 with the v3 JSON format, where the two conventions already present in `en.json` (`_one`/
 * `_other` and `_plural`) do not both resolve.
 */
const MESSAGE_COPY = {
  compact: {
    single: { key: "calendar.blackout.booking_requires_action", fallback: "1 booking requires action" },
    plural: { key: "calendar.blackout.bookings_require_action", fallback: "{{count}} bookings require action" },
    unknown: { key: "calendar.blackout.bookings_require_action_unknown", fallback: "Bookings require action" }
  },
  full: {
    single: {
      key: "calendar.blackout.booking_requires_rescheduling",
      fallback: "1 booking requires cancellation or rescheduling"
    },
    plural: {
      key: "calendar.blackout.bookings_require_rescheduling",
      fallback: "{{count}} bookings require cancellation or rescheduling"
    },
    unknown: {
      key: "calendar.blackout.bookings_require_rescheduling_unknown",
      fallback: "Bookings require cancellation or rescheduling"
    }
  }
}

/**
 * Which of the three wordings this count can support.
 *
 * The feed currently answers `bookings_requiring_action` with `true` rather than a number — it
 * knows the day has work on it but not how much. Interpolating that would read "true bookings
 * require action", so a non-numeric truthy value drops to the countless wording instead. Delete
 * the `unknown` branch once the field is a number everywhere.
 */
const pickCopy = (count, isCompact) => {
  const variant = MESSAGE_COPY[isCompact ? "compact" : "full"]
  if (typeof count !== "number" || !Number.isFinite(count)) return variant.unknown
  return count === 1 ? variant.single : variant.plural
}

/**
 * "⚠ N bookings require action" — the red-surface row shown on a blackout day card, in the
 * day details panel and in the day-view banner.
 *
 * `onReview` adds the "Review ↗" link used where the design offers a jump to the affected
 * reservations; without it the row is informational only.
 *
 * One surface everywhere — the red one the designs give it on both the calendar and the
 * settings rows.
 */
const BookingsActionAlert = ({ count, onReview, isCompact = false, className = "" }) => {
  const { t } = useTranslation()

  if (!count) return null

  const copy = pickCopy(count, isCompact)
  const message = t(copy.key, { defaultValue: copy.fallback, count })

  return (
    <div className={`${styles.alert} ${isCompact ? styles.compact : ""} ${className}`}>
      <Icon
        name="AlertTriangle"
        width={isCompact ? 16 : 20}
        height={isCompact ? 16 : 20}
        color="var(--color-error)"
      />
      <span className={styles.message}>{message}</span>
      {onReview && (
        <button type="button" className={styles.reviewLink} onClick={onReview}>
          {t("calendar.blackout.review", { defaultValue: "Review" })}
          <Icon name="ExternalLink" width={12} height={12} color="currentColor" />
        </button>
      )}
    </div>
  )
}

BookingsActionAlert.propTypes = {
  /** A number states how many; `true` only that there are some, which is all the feed sends today. */
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  onReview: PropTypes.func,
  /** Shortens the copy for the narrow month day card. */
  isCompact: PropTypes.bool,
  className: PropTypes.string
}

export default BookingsActionAlert
